import { useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { Button } from '../../components/ui';
import { InlineNumberInput } from '../../components/shared';
import { formatCents } from '../../utils/growthProjection';
import { getAllocationDescription } from '../../utils/allocationDescriptions';
import { recalculateBalances } from '../../utils/balanceCalculator';
import { track, EVENTS } from '../../utils/analytics';
import type { MapNode, MapAction } from '../../types';

function getNodeEmoji(node: MapNode): string {
  if (node.icon) return node.icon;
  const emojiMap: Record<string, string> = {
    CHECKING: '🏦',
    SAVINGS: '🐷',
    CREDIT_CARD: '💳',
    MORTGAGE: '🏠',
    BROKERAGE: '📈',
    IRA: '🏖️',
    PENSION: '🏖️',
    EDUCATION_SAVINGS: '🎓',
    PERSONAL_LOAN: '📋',
    STUDENT_LOAN: '🎓',
    AUTO_LOAN: '🚗',
    BUSINESS_LOAN: '💼',
    HOME_LOAN: '🏠',
  };
  return emojiMap[node.subtype || ''] || '💰';
}

function computeMonthlyDollars(action: MapAction, sourceBalance: number): number {
  switch (action.type) {
    case 'PERCENTAGE':
      return Math.round((action.amountInPercentage / 100) * sourceBalance);
    case 'FIXED':
    case 'TOP_UP':
      return action.amountInCents;
    case 'AVALANCHE':
    case 'SNOWBALL':
      if (action.amountInPercentage) {
        return Math.round((action.amountInPercentage / 100) * sourceBalance);
      }
      return action.amountInCents;
    default:
      return 0;
  }
}

interface AllocationCard {
  key: string;
  node: MapNode;
  action: MapAction;
  ruleIdx: number;
  stepIdx: number;
  actionIdx: number;
  isRemainder: boolean;
  monthlyCents: number;
  sourceBalance: number;
}

export default function PlanBreakdown() {
  const navigate = useNavigate();
  const { state, updateMapData } = useApp();
  const { api, flow } = state;
  const isExpress = flow === 'express';

  useEffect(() => {
    if (api.status !== 'success' || !api.result) {
      navigate('/quiz', { replace: true });
    }
  }, [api.status, api.result, navigate]);

  useEffect(() => {
    track(EVENTS.PLAN_PAGE_VIEWED, { flow });
  }, []);

  const mapData = api.result?.map;

  interface RuleSection {
    ruleIdx: number;
    sourceNode: MapNode;
    cards: AllocationCard[];
  }

  const sections: RuleSection[] = useMemo(() => {
    if (!mapData) return [];

    const nMap = new Map<string, MapNode>();
    for (const node of mapData.nodes) {
      nMap.set(node.id, node);
    }

    const result: RuleSection[] = [];

    for (let ruleIdx = 0; ruleIdx < mapData.rules.length; ruleIdx++) {
      const rule = mapData.rules[ruleIdx];
      const sourceNode = nMap.get(rule.sourceId);
      if (!sourceNode) continue;

      const allActions = rule.steps.flatMap((s) => s.actions);
      const minGroup = Math.min(...allActions.map((a) => a.groupIndex));

      const sectionCards: AllocationCard[] = [];

      for (let stepIdx = 0; stepIdx < rule.steps.length; stepIdx++) {
        const step = rule.steps[stepIdx];
        for (let actionIdx = 0; actionIdx < step.actions.length; actionIdx++) {
          const action = step.actions[actionIdx];
          const destNode = nMap.get(action.destinationId);
          if (!destNode) continue;

          const isRemainder = action.groupIndex > minGroup;
          const isSweep = isRemainder && action.type === 'PERCENTAGE' && action.amountInPercentage === 100;
          const monthlyCents = computeMonthlyDollars(action, sourceNode.balance);

          sectionCards.push({
            key: `${ruleIdx}-${stepIdx}-${actionIdx}`,
            node: destNode,
            action,
            ruleIdx,
            stepIdx,
            actionIdx,
            isRemainder: isSweep,
            monthlyCents,
            sourceBalance: sourceNode.balance,
          });
        }
      }

      if (sectionCards.length > 0) {
        result.push({ ruleIdx, sourceNode, cards: sectionCards });
      }
    }

    return result;
  }, [mapData]);

  const handleEdit = useCallback((
    ruleIdx: number,
    stepIdx: number,
    actionIdx: number,
    field: 'amountInPercentage' | 'amountInCents',
    value: number,
  ) => {
    if (!mapData) return;

    const clone = structuredClone(mapData);
    const action = clone.rules[ruleIdx]?.steps[stepIdx]?.actions[actionIdx];
    if (!action) return;

    const oldValue = action[field];
    action[field] = value;

    track(EVENTS.PLAN_AMOUNT_EDITED, {
      flow,
      actionType: action.type,
      oldValue,
      newValue: value,
    });

    const updated = recalculateBalances(clone);
    updateMapData(updated);
  }, [mapData, updateMapData]);

  // Compute total monthly income from PORT nodes
  const totalMonthlyIncome = useMemo(() => {
    if (!mapData) return 0;
    const portNode = mapData.nodes.find((n) => n.type === 'PORT');
    return portNode?.balance || 0;
  }, [mapData]);

  if (!api.result || !mapData) {
    return null;
  }

  const playgroundId = api.result.id;

  return (
    <div className="plan-page">
      <div className="plan-page__container">
        {/* Header */}
        <div className="plan-page__header">
          <h1 className="plan-page__title">Your Money, Step by Step</h1>
          <p className="plan-page__subtitle">
            {isExpress
              ? 'Here\'s exactly how your financial setup distributes every dollar.'
              : 'Here\'s exactly where every dollar goes - and why each move matters.'}
          </p>
        </div>

        {/* Allocation Steps - grouped by rule */}
        {sections.map((section, sectionIdx) => {
          const isFirstSection = sectionIdx === 0;
          const sectionIntro = isFirstSection
            ? 'When your income arrives, here\'s how it flows:'
            : `Of the money allocated to ${section.sourceNode.name}, distribute:`;

          return (
            <div key={section.ruleIdx} className="plan-section">
              <p className="plan-steps__intro">{sectionIntro}</p>
              <div className="plan-steps">
                {section.cards.map((card, index) => {
                  const desc = getAllocationDescription(
                    card.node.type,
                    card.node.subtype ?? undefined,
                    card.node.name,
                    card.isRemainder,
                  );

                  const percentDisplay = card.action.type === 'PERCENTAGE'
                    ? `${card.action.amountInPercentage}%`
                    : null;

                  const monthlyDisplay = formatCents(card.monthlyCents);

                  const isEditable = card.action.type === 'PERCENTAGE'
                    || card.action.type === 'FIXED'
                    || card.action.type === 'TOP_UP'
                    || card.action.type === 'AVALANCHE'
                    || card.action.type === 'SNOWBALL';

                  const isSweep = card.isRemainder;
                  const isLast = index === section.cards.length - 1;

                  return (
                    <div className={`plan-step${isLast ? ' plan-step--last' : ''}`} key={card.key}>
                      <div className="plan-step__indicator">
                        <span className="plan-step__number">{index + 1}</span>
                        <span className="plan-step__line" />
                      </div>
                      <div className="plan-step__content">
                        <div className="plan-step__header">
                          <span className="plan-step__emoji">{getNodeEmoji(card.node)}</span>
                          <span className="plan-step__name">{card.node.name}</span>
                        </div>
                        <p className="plan-step__explanation">{desc.explanation}</p>
                        <div className="plan-step__details">
                          {isEditable && !isSweep ? (
                            <span className="plan-step__allocation">
                              <InlineNumberInput
                                value={card.action.type === 'PERCENTAGE' || card.action.type === 'AVALANCHE' || card.action.type === 'SNOWBALL'
                                  ? (card.action.amountInPercentage || card.action.amountInCents)
                                  : card.action.amountInCents}
                                type={card.action.type === 'PERCENTAGE' || (card.action.amountInPercentage && (card.action.type === 'AVALANCHE' || card.action.type === 'SNOWBALL'))
                                  ? 'percent'
                                  : 'dollars'}
                                onChange={(v) => {
                                  const field = card.action.type === 'PERCENTAGE' || (card.action.amountInPercentage && (card.action.type === 'AVALANCHE' || card.action.type === 'SNOWBALL'))
                                    ? 'amountInPercentage'
                                    : 'amountInCents';
                                  handleEdit(card.ruleIdx, card.stepIdx, card.actionIdx, field, v);
                                }}
                              />
                              <span className="plan-step__monthly">{monthlyDisplay}/mo</span>
                            </span>
                          ) : (
                            <span className="plan-step__allocation">
                              {isSweep ? (
                                <span className="plan-step__badge">Remainder</span>
                              ) : percentDisplay ? (
                                <span className="plan-step__badge">{percentDisplay}</span>
                              ) : (
                                <span className="plan-step__badge">{monthlyDisplay}/mo</span>
                              )}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Total Bar */}
        <div className="plan-total">
          <span className="plan-total__label">Your Total Monthly Income</span>
          <span className="plan-total__percent">100%</span>
          <span className="plan-total__amount">{formatCents(totalMonthlyIncome)}/mo</span>
        </div>

        {/* CTAs */}
        <div className="plan-ctas">
          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate(`/results/${playgroundId}`)}
          >
            Back to Map
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              track(EVENTS.ACTIVATE_BLUEPRINT_CLICKED, { flow });
              navigate('/activate');
            }}
          >
            {isExpress ? 'Activate My Setup' : 'Activate My Blueprint'}
          </Button>
        </div>
      </div>
    </div>
  );
}
